import React from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, errorId: Math.random().toString(36).slice(2, 10).toUpperCase() };
  }

  componentDidCatch(error, info) {
    // Best-effort reporting — don't block the UI
    try {
      axios.post(`${API_BASE}/api/errors/client`, {
        message: error?.message || String(error),
        stack: error?.stack || "",
        component_stack: info?.componentStack || "",
        location: typeof window !== "undefined" ? window.location.href : "",
      }, { withCredentials: true, timeout: 5000 }).catch(() => {});
    } catch { /* ignore */ }
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  handleReset = () => { this.setState({ hasError: false, error: null, errorId: null }); };
  handleReload = () => { window.location.reload(); };
  handleHome = () => { window.location.href = "/"; };

  render() {
    if (!this.state.hasError) return this.props.children;

    const name = this.props.label || "This page";
    const msg = this.state.error?.message || "An unexpected error occurred";
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{name} hit a snag</h1>
              <p className="text-xs text-slate-500">Error ID: <span className="font-mono">{this.state.errorId}</span></p>
            </div>
          </div>

          <p className="text-sm text-slate-600 mb-5">We&apos;ve logged this automatically and our team has been notified. You can try again or head home.</p>

          <details className="text-xs text-slate-400 mb-5 bg-slate-50 rounded-lg px-3 py-2">
            <summary className="cursor-pointer select-none">Technical details</summary>
            <p className="mt-2 font-mono break-words">{msg}</p>
          </details>

          <div className="flex gap-2 flex-wrap">
            <button onClick={this.handleReset} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium transition-colors" data-testid="error-retry-btn">Try again</button>
            <button onClick={this.handleReload} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors" data-testid="error-reload-btn">Reload</button>
            <button onClick={this.handleHome} className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors" data-testid="error-home-btn">Go home</button>
          </div>

          <p className="text-[10px] text-slate-400 mt-5 text-center">Need help? Contact support with the error ID above.</p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
